import { useState, useEffect } from 'react'
import { TrendingUp, TrendingDown, ChevronDown, ChevronUp, CheckCircle, AlertCircle } from 'lucide-react'
import { supabase } from '../services/supabase'
import DetalleProductoModal from './DetalleProductoModal'

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])
  return isMobile
}

function Sparkline({ data, color }) {
  if (!data || data.length < 2) return null
  const max = Math.max(...data)
  const min = Math.min(...data)
  const range = max - min || 1
  const w = 100, h = 36, pad = 3
  const pts = data.map((v, i) => {
    const x = pad + (i / (data.length - 1)) * (w - pad * 2)
    const y = pad + (1 - (v - min) / range) * (h - pad * 2)
    return `${x},${y}`
  }).join(' ')

  // Área rellena debajo de la línea
  const first = `${pad},${h - pad}`
  const last = `${w - pad},${h - pad}`
  const fillPts = `${first} ${pts} ${last}`

  return (
    <svg width={w} height={h} style={{ display: 'block', flexShrink: 0 }}>
      <polygon
        points={fillPts}
        fill={color}
        opacity="0.15"
      />
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function fmt(n) {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2
  }).format(n)
}

export default function ResumenDelDia({ deliveries, kpis, clientPricesMap = {} }) {
  const isMobile = useIsMobile()
  const [ventasAyer, setVentasAyer] = useState(null)
  const [sparkline, setSparkline] = useState([])
  const [expandido, setExpandido] = useState(false)
  const [modalDetalle, setModalDetalle] = useState(null)

  const hoy = new Date().toISOString().split('T')[0]
  const deliveriesHoy = deliveries?.filter(d => d.fecha === hoy) || []

  // ── Calcular totales del día ──────────────────────────────────────────────
  let totalVentas = 0
  let totalDevoluciones = 0
  const productoStats = {}
  const clienteStats = {}

  deliveriesHoy.forEach(delivery => {
    const clienteNombre = delivery.clients?.alias || delivery.clients?.razon_social || 'Desconocido'
    const clientId = delivery.client_id
    const descuentoPct = delivery.clients?.descuento_porcentaje || 0
    if (!clienteStats[clienteNombre]) clienteStats[clienteNombre] = { nombre: clienteNombre, ventas: 0, ventasBruto: 0, devoluciones: 0, productos: [] }

    delivery.delivery_lines?.forEach(line => {
      const nombre = line.products?.nombre || 'Desconocido'
      const entregado = line.cantidad_entregada || 0
      const devuelto = line.cantidad_devuelta || 0
      const cantidadNeta = entregado - devuelto

      // Precio especial del cliente primero, si no el del albarán
      const precioBase = clientPricesMap[clientId]?.[line.product_id] ?? line.precio_unit ?? 0
      // Descuento solo si el producto lo permite
      const descuentoAplicable = line.descuento_aplicable ?? false
      const precio = descuentoAplicable ? precioBase * (1 - descuentoPct / 100) : precioBase

      const importeEntregaBruto = entregado * precioBase
      const importeNeto = cantidadNeta > 0 ? cantidadNeta * precio : 0
      const importeDev = devuelto * precioBase

      totalVentas += importeNeto
      totalDevoluciones += importeDev

      if (!productoStats[nombre]) productoStats[nombre] = { nombre, entregado: 0, devuelto: 0, clientes: [] }
      productoStats[nombre].entregado += entregado
      productoStats[nombre].devuelto += devuelto
      if (entregado > 0) productoStats[nombre].clientes.push({ nombre: clienteNombre, entregado, devuelto, porcentajeDev: entregado > 0 ? ((devuelto / entregado) * 100).toFixed(1) : '0.0' })

      clienteStats[clienteNombre].ventas      += importeNeto
      clienteStats[clienteNombre].ventasBruto += importeEntregaBruto
      clienteStats[clienteNombre].devoluciones += importeDev
      if (devuelto > 0) clienteStats[clienteNombre].productos.push({ producto: nombre, entregado, devuelto, porcentaje: entregado > 0 ? ((devuelto / entregado) * 100).toFixed(1) : '0.0' })
    })
  })

  const totalVentasBruto = Object.values(clienteStats).reduce((acc, c) => acc + c.ventasBruto, 0)
  const totalNeto = totalVentas
  const pctDev = totalVentasBruto > 0 ? (totalDevoluciones / totalVentasBruto) * 100 : 0
  const colorEstado = pctDev > 12 ? '#ef4444' : pctDev > 8 ? '#f59e0b' : '#10b981'

  // Productos estrella y con sobras
  const productosArray = Object.values(productoStats).map(p => ({
    ...p,
    porcentaje: p.entregado > 0 ? ((p.devuelto / p.entregado) * 100).toFixed(1) : '0.0'
  }))
  const topProductos = productosArray.filter(p => p.entregado > 0).sort((a, b) => b.entregado - a.entregado).slice(0, 3)
  const productosConSobras = productosArray.filter(p => parseFloat(p.porcentaje) > 10 && p.devuelto > 5).sort((a, b) => parseFloat(b.porcentaje) - parseFloat(a.porcentaje)).slice(0, 5)
  const clientesConProblemas = Object.values(clienteStats).map(c => ({
    ...c,
    porcentajeDev: c.ventasBruto > 0 ? ((c.devoluciones / c.ventasBruto) * 100).toFixed(1) : '0.0'
  })).filter(c => parseFloat(c.porcentajeDev) > 15 && c.productos.length > 0).sort((a, b) => parseFloat(b.porcentajeDev) - parseFloat(a.porcentajeDev)).slice(0, 5)

  // ── Fetch ayer + sparkline ────────────────────────────────────────────────
  useEffect(() => {
    fetchAyer()
    fetchSparkline()
  }, [])

  async function fetchAyer() {
    const ayer = new Date()
    ayer.setDate(ayer.getDate() - 1)
    const ayerStr = ayer.toISOString().split('T')[0]
    const { data } = await supabase
      .from('deliveries')
      .select('delivery_lines(cantidad_entregada, cantidad_devuelta, precio_unit)')
      .eq('fecha', ayerStr)
    let neto = 0
    data?.forEach(d => d.delivery_lines?.forEach(l => {
      neto += ((l.cantidad_entregada || 0) - (l.cantidad_devuelta || 0)) * (l.precio_unit || 0)
    }))
    setVentasAyer(neto)
  }

  async function fetchSparkline() {
    const desde = new Date()
    desde.setDate(desde.getDate() - 6)
    const { data } = await supabase
      .from('deliveries')
      .select('fecha, delivery_lines(cantidad_entregada, cantidad_devuelta, precio_unit)')
      .gte('fecha', desde.toISOString().split('T')[0])
      .order('fecha')
    const porDia = {}
    data?.forEach(d => {
      if (!porDia[d.fecha]) porDia[d.fecha] = 0
      d.delivery_lines?.forEach(l => {
        porDia[d.fecha] += ((l.cantidad_entregada || 0) - (l.cantidad_devuelta || 0)) * (l.precio_unit || 0)
      })
    })
    setSparkline(Object.values(porDia))
  }

  // ── Comparativas (sobre neto) ─────────────────────────────────────────────
  const pctVsAyer = ventasAyer !== null && ventasAyer > 0
    ? ((totalNeto - ventasAyer) / ventasAyer) * 100
    : null
  const pctVsPeriodo = kpis?.ventasNetas > 0
    ? (totalNeto / kpis.ventasNetas) * 100
    : null

  // ── Sin datos de hoy ──────────────────────────────────────────────────────
  if (deliveriesHoy.length === 0) {
    return (
      <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem', textAlign: 'center', color: 'var(--muted)', borderLeft: '4px solid var(--muted)' }}>
        📊 Sin datos de repartos para hoy todavía
      </div>
    )
  }

  const fechaFormateada = new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <>
      <div
        className="card"
        style={{
          marginBottom: '1.5rem',
          border: `2px solid ${colorEstado}`,
          overflow: 'hidden'
        }}
      >
        {/* ── HERO ── */}
        <div style={{ padding: isMobile ? '1.25rem' : '1.75rem' }}>
          {/* Cabecera fecha */}
          <div style={{
            fontSize: '0.75rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--muted)',
            marginBottom: '0.5rem'
          }}>
            HOY · {fechaFormateada}
          </div>

          {/* Número hero (NETO) + sparkline */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', marginBottom: '0.5rem' }}>
            <div>
              <div style={{
                fontSize: isMobile ? '2.2rem' : '2.8rem',
                fontWeight: 800,
                lineHeight: 1,
                letterSpacing: '-0.02em',
                marginBottom: '0.1rem',
                color: '#10b981'
              }}>
                {fmt(totalNeto)}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>ventas netas del día</div>
            </div>
            {sparkline.length >= 2 && (
              <div style={{ paddingTop: '0.25rem' }}>
                <Sparkline data={sparkline} color={colorEstado} />
                <div style={{ fontSize: '0.65rem', color: 'var(--muted)', textAlign: 'center', marginTop: '0.2rem' }}>7 días</div>
              </div>
            )}
          </div>

          {/* Entregas brutas (secundario) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem' }}>
            <TrendingUp size={14} color="var(--muted)" />
            <span style={{ fontSize: '0.875rem', color: 'var(--muted)' }}>{fmt(totalVentas)}</span>
            <span style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>entregado</span>
          </div>

          {/* Devoluciones */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '1rem',
            flexWrap: 'wrap'
          }}>
            <TrendingDown size={14} color={colorEstado} />
            <span style={{ fontSize: '0.875rem', fontWeight: 700, color: colorEstado }}>−{fmt(totalDevoluciones)}</span>
            <span style={{
              fontSize: '0.78rem',
              padding: '0.15rem 0.45rem',
              borderRadius: '4px',
              background: `${colorEstado}22`,
              color: colorEstado,
              fontWeight: 700
            }}>
              {pctDev.toFixed(1)}% dev
            </span>
          </div>

          {/* Separador */}
          <div style={{ height: '1px', background: 'var(--line)', marginBottom: '0.85rem' }} />

          {/* Comparativas */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {pctVsAyer !== null && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
                <span style={{
                  fontWeight: 700,
                  color: pctVsAyer >= 0 ? '#10b981' : '#ef4444',
                  minWidth: '60px'
                }}>
                  {pctVsAyer >= 0 ? '↗' : '↘'} {Math.abs(pctVsAyer).toFixed(1)}%
                </span>
                <span style={{ color: 'var(--muted)' }}>
                  vs ayer (neto {fmt(ventasAyer)})
                </span>
              </div>
            )}
            {pctVsPeriodo !== null && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
                <span style={{ fontWeight: 700, color: 'var(--muted)', minWidth: '60px' }}>
                  {pctVsPeriodo.toFixed(1)}%
                </span>
                <span style={{ color: 'var(--muted)' }}>del total del período</span>
              </div>
            )}
          </div>
        </div>

        {/* ── TOGGLE detalle ── */}
        <div
          onClick={() => setExpandido(!expandido)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.4rem',
            padding: '0.6rem',
            cursor: 'pointer',
            background: 'var(--bg-secondary)',
            fontSize: '0.8rem',
            color: 'var(--muted)',
            borderTop: '1px solid var(--line)'
          }}
        >
          {expandido ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          {expandido ? 'Ocultar detalle' : 'Ver detalle del día'}
        </div>

        {/* ── DETALLE EXPANDIDO ── */}
        {expandido && (
          <div style={{ padding: isMobile ? '1rem' : '1.5rem', borderTop: '1px solid var(--line)' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: '1rem'
            }}>
              {/* Top productos por volumen */}
              <div>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#10b981', marginBottom: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <CheckCircle size={14} color="#10b981" />
                  Mayor volumen hoy
                </div>
                {topProductos.length > 0 ? topProductos.map((p, i) => (
                  <div
                    key={i}
                    onClick={() => setModalDetalle({ producto: p.nombre, fecha: hoy, datosClientes: p.clientes })}
                    style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', background: 'rgba(16,185,129,0.08)', marginBottom: '0.35rem', cursor: 'pointer', fontSize: '0.875rem' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>{p.nombre}</span>
                      <span style={{ color: '#10b981', fontWeight: 700 }}>{p.entregado} ud</span>
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>
                      {p.devuelto > 0 ? `−${p.devuelto} ud dev (${p.porcentaje}%)` : 'Sin devoluciones'}
                    </div>
                  </div>
                )) : (
                  <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>Sin datos</div>
                )}
              </div>

              {/* Productos con sobras */}
              <div>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#ef4444', marginBottom: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <AlertCircle size={14} color="#ef4444" />
                  Con sobras
                </div>
                {productosConSobras.length > 0 ? productosConSobras.map((p, i) => (
                  <div
                    key={i}
                    onClick={() => setModalDetalle({ producto: p.nombre, fecha: hoy, datosClientes: p.clientes })}
                    style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', background: 'rgba(239,68,68,0.08)', marginBottom: '0.35rem', fontSize: '0.875rem', cursor: 'pointer' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>{p.nombre}</span>
                      <span style={{ color: '#ef4444', fontWeight: 700 }}>{p.porcentaje}%</span>
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>{p.devuelto}/{p.entregado} ud</div>
                  </div>
                )) : (
                  <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>✅ Sin sobras significativas</div>
                )}
              </div>

              {/* Clientes con problemas */}
              <div>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#f59e0b', marginBottom: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <AlertCircle size={14} color="#f59e0b" />
                  Clientes con devoluciones
                </div>
                {clientesConProblemas.length > 0 ? clientesConProblemas.map((c, i) => (
                  <div key={i} style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', background: 'rgba(245,158,11,0.08)', marginBottom: '0.35rem', fontSize: '0.875rem' }}>
                    <div style={{ fontWeight: 700 }}>{c.nombre} <span style={{ color: '#f59e0b' }}>{c.porcentajeDev}%</span></div>
                    {c.productos.slice(0, 2).map((prod, j) => (
                      <div key={j} style={{ fontSize: '0.75rem', color: 'var(--muted)', marginLeft: '0.5rem' }}>
                        · {prod.producto}: {prod.porcentaje}% (−{prod.devuelto} ud)
                      </div>
                    ))}
                  </div>
                )) : (
                  <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>✅ Todos los clientes bien</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {modalDetalle && (
        <DetalleProductoModal
          producto={modalDetalle.producto}
          fecha={modalDetalle.fecha}
          datosClientes={modalDetalle.datosClientes}
          onClose={() => setModalDetalle(null)}
        />
      )}
    </>
  )
}
